import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Send, Settings, ChevronDown, ChevronRight, X, User, Bot, Wrench, Workflow, AlertCircle, Loader } from 'lucide-react';

// Force cache busting - version 2.0.1
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';

const ChatRenderer = ({ definition, agentId, token, onClose, onNavigate }) => {
  // State management
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [selectedModel, setSelectedModel] = useState('gpt-4');
  const [sessionId, setSessionId] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [wsConnection, setWsConnection] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [currentWorkflow, setCurrentWorkflow] = useState(null);
  const [expandedSteps, setExpandedSteps] = useState(new Set());
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const wsRef = useRef(null);

  // Provider configurations
  const providers = {
    openai: {
      name: 'OpenAI',
      models: ['gpt-4', 'gpt-4-turbo', 'gpt-3.5-turbo'],
      icon: '🤖'
    },
    anthropic: {
      name: 'Anthropic',
      models: ['claude-3-opus-20240229', 'claude-3-sonnet-20240229', 'claude-3-haiku-20240307'],
      icon: '🧠'
    },
    google: {
      name: 'Google',
      models: ['gemini-1.5-pro', 'gemini-1.5-flash', 'gemini-pro'],
      icon: '🔮'
    },
    azure: {
      name: 'Azure OpenAI',
      models: ['gpt-4', 'gpt-35-turbo'],
      icon: '☁️'
    }
  };

  // Initialize chat session
  useEffect(() => {
    initializeSession();
    loadAvailableWorkflows();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [agentId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initializeSession = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          agent_id: agentId,
          session_name: `Chat Session - ${new Date().toLocaleString()}`,
          metadata: {
            provider: selectedProvider,
            model: selectedModel
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session_id);
        connectWebSocket(data.session_id);
        
        // Load chat history for this session
        await loadChatHistory(data.session_id);
        
        // If no messages loaded, add welcome message
        if (!data.messages || data.messages.length === 0) {
          setMessages(prev => prev.length === 0 ? [{
            id: 'welcome',
            type: 'system',
            content: `Welcome to the Chat Playground! You can interact with workflows, ask questions, and execute tools. Select a workflow from the sidebar or type your message below.`,
            timestamp: new Date().toISOString()
          }] : prev);
        }
      }
    } catch (error) {
      console.error('Failed to initialize session:', error);
      setMessages([{
        id: 'error',
        type: 'error',
        content: 'Failed to initialize chat session. Please refresh the page.',
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const loadChatHistory = async (sessionId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/sessions/${sessionId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const formattedMessages = data.messages.map(msg => ({
          id: msg.message_id,
          type: msg.message_type,
          content: msg.message_content,
          metadata: msg.message_metadata,
          timestamp: msg.created_at
        }));
        setMessages(formattedMessages);
        console.log('Chat history loaded:', formattedMessages.length, 'messages');
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const loadAvailableWorkflows = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/agents/${agentId}/workflows`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setWorkflows(data.workflows || []);
      }
    } catch (error) {
      console.error('Failed to load workflows:', error);
    }
  };

  const connectWebSocket = (sessionId) => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // Use the same hostname as the current page (no port needed with nginx proxy)
    const wsUrl = `${wsProtocol}//${window.location.host}/api/chat/ws/${sessionId}?token=${token}`;
    
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      setConnectionStatus('connected');
      console.log('WebSocket connected');
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleWebSocketMessage(data);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setConnectionStatus('error');
    };

    ws.onclose = () => {
      setConnectionStatus('disconnected');
      console.log('WebSocket disconnected');
      // Attempt to reconnect after 3 seconds
      setTimeout(() => {
        if (sessionId) {
          connectWebSocket(sessionId);
        }
      }, 3000);
    };

    wsRef.current = ws;
    setWsConnection(ws);
  };

  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case 'message':
        setMessages(prev => [...prev, {
          id: data.message_id,
          type: data.message_type,
          content: data.content,
          metadata: data.metadata,
          timestamp: data.timestamp
        }]);
        break;

      case 'assistant_message':
        setMessages(prev => [...prev, {
          id: data.message_id,
          type: data.message_type || 'assistant',
          content: data.content,
          metadata: data.metadata,
          timestamp: data.timestamp
        }]);
        setIsLoading(false);
        break;

      case 'workflow_step':
        setMessages(prev => [...prev, {
          id: data.step_id,
          type: 'workflow_step',
          stepName: data.step_name,
          status: data.status,
          content: data.content,
          result: data.result,
          timestamp: data.timestamp
        }]);
        break;

      case 'tool_result':
        setMessages(prev => [...prev, {
          id: data.result_id,
          type: 'tool_result',
          toolName: data.tool_name,
          content: data.content,
          result: data.result,
          timestamp: data.timestamp
        }]);
        break;

      case 'error':
        setMessages(prev => [...prev, {
          id: data.error_id,
          type: 'error',
          content: data.message,
          timestamp: data.timestamp
        }]);
        setIsLoading(false);
        break;

      case 'status':
        if (data.status === 'completed') {
          setIsLoading(false);
        }
        break;

      case 'connection':
        // Handle WebSocket connection confirmation
        console.log('WebSocket connection confirmed:', data);
        break;

      default:
        console.log('Unknown message type:', data.type);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !sessionId) return;

    // Store message before clearing input
    const messageContent = inputMessage;
    
    const userMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: messageContent,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: messageContent,  // Use stored message content
          provider: selectedProvider,
          model: selectedModel,
          workflow_id: currentWorkflow?.id,
          stream: false  // Add stream parameter
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const data = await response.json();
      console.log('Chat API response:', data);

      // Add AI response to messages
      if (data.status === 'success' && data.message) {
        const aiMessage = {
          id: data.message.id || `ai-${Date.now()}`,
          type: 'assistant',
          content: data.message.content,
          timestamp: data.message.timestamp || new Date().toISOString(),
          metadata: data.message.metadata
        };
        setMessages(prev => [...prev, aiMessage]);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Send message error:', error);
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        type: 'error',
        content: 'Failed to send message. Please try again.',
        timestamp: new Date().toISOString()
      }]);
      setIsLoading(false);
    }
  };

  const executeWorkflow = async (workflow) => {
    setCurrentWorkflow(workflow);
    setIsLoading(true);

    const workflowMessage = {
      id: `workflow-${Date.now()}`,
      type: 'system',
      content: `Starting workflow: ${workflow.name}`,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, workflowMessage]);

    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/workflows/execute`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: sessionId,
          workflow_id: workflow.id,
          provider: selectedProvider,
          model: selectedModel
        })
      });

      if (!response.ok) {
        throw new Error('Failed to execute workflow');
      }
    } catch (error) {
      console.error('Execute workflow error:', error);
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        type: 'error',
        content: `Failed to execute workflow: ${workflow.name}`,
        timestamp: new Date().toISOString()
      }]);
      setIsLoading(false);
    }
  };

  const toggleStepExpansion = (stepId) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stepId)) {
        newSet.delete(stepId);
      } else {
        newSet.add(stepId);
      }
      return newSet;
    });
  };

  const renderMessage = (message) => {
    switch (message.type) {
      case 'user':
        return (
          <div className="flex justify-end mb-4">
            <div className="max-w-[70%] bg-blue-50 dark:bg-blue-900/20 text-gray-900 dark:text-gray-100 rounded-lg px-4 py-2 border border-blue-200 dark:border-blue-700">
              <div className="flex items-center gap-2 mb-1">
                <User size={14} className="text-blue-600 dark:text-blue-400" />
                <span className="text-xs text-blue-700 dark:text-blue-300">You</span>
              </div>
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
            </div>
          </div>
        );

      case 'assistant':
        return (
          <div className="flex justify-start mb-4">
            <div className="max-w-[70%] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg px-4 py-2 border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <Bot size={14} className="text-green-600 dark:text-green-400" />
                <span className="text-xs text-green-700 dark:text-green-300">Assistant ({selectedModel})</span>
                {message.metadata?.tokens_used && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    • {message.metadata.tokens_used} tokens
                  </span>
                )}
              </div>
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
            </div>
          </div>
        );

      case 'workflow_step':
        return (
          <div className="flex justify-start mb-4">
            <div className="max-w-[90%] bg-purple-50 dark:bg-purple-900/20 rounded-lg px-4 py-2 border border-purple-200 dark:border-purple-700">
              <div 
                className="flex items-center gap-2 mb-1 cursor-pointer"
                onClick={() => toggleStepExpansion(message.id)}
              >
                <Workflow size={14} className="text-purple-600" />
                <span className="text-xs font-medium text-purple-700 dark:text-purple-300">
                  Workflow Step: {message.stepName}
                </span>
                {message.status && (
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    message.status === 'completed' ? 'bg-green-100 text-green-700' :
                    message.status === 'failed' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {message.status}
                  </span>
                )}
                {expandedSteps.has(message.id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </div>
              <p className="text-sm">{message.content}</p>
              {expandedSteps.has(message.id) && message.result && (
                <div className="mt-2 p-2 bg-white dark:bg-gray-900 rounded">
                  <pre className="text-xs overflow-x-auto">
                    {typeof message.result === 'string' ? message.result : JSON.stringify(message.result, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        );

      case 'tool_result':
        return (
          <div className="flex justify-start mb-4">
            <div className="max-w-[90%] bg-green-50 dark:bg-green-900/20 rounded-lg px-4 py-2 border border-green-200 dark:border-green-700">
              <div 
                className="flex items-center gap-2 mb-1 cursor-pointer"
                onClick={() => toggleStepExpansion(message.id)}
              >
                <Wrench size={14} className="text-green-600" />
                <span className="text-xs font-medium text-green-700 dark:text-green-300">
                  Tool: {message.toolName}
                </span>
                {expandedSteps.has(message.id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </div>
              <p className="text-sm">{message.content}</p>
              {expandedSteps.has(message.id) && message.result && (
                <div className="mt-2 p-2 bg-white dark:bg-gray-900 rounded">
                  <pre className="text-xs overflow-x-auto">
                    {typeof message.result === 'string' ? message.result : JSON.stringify(message.result, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        );

      case 'system':
        return (
          <div className="flex justify-center mb-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg px-4 py-2 border border-blue-200 dark:border-blue-700">
              <p className="text-xs text-blue-700 dark:text-blue-300 text-center">
                {message.content}
              </p>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="flex justify-center mb-4">
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg px-4 py-2 border border-red-200 dark:border-red-700">
              <div className="flex items-center gap-2">
                <AlertCircle size={14} className="text-red-600" />
                <p className="text-xs text-red-700 dark:text-red-300">
                  {message.content}
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex h-full bg-white dark:bg-gray-900">
      {/* Sidebar */}
      <div className="w-64 border-r border-gray-200 dark:border-gray-700 flex flex-col">
        {/* Provider Settings */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium">AI Provider</h3>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
            >
              <Settings size={16} />
            </button>
          </div>
          
          {showSettings && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400">Provider</label>
                <select
                  value={selectedProvider}
                  onChange={(e) => {
                    setSelectedProvider(e.target.value);
                    setSelectedModel(providers[e.target.value].models[0]);
                  }}
                  className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-gray-800"
                >
                  {Object.entries(providers).map(([key, provider]) => (
                    <option key={key} value={key}>
                      {provider.icon} {provider.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400">Model</label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-gray-800"
                >
                  {providers[selectedProvider].models.map(model => (
                    <option key={model} value={model}>{model}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Workflows List */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4">
            <h3 className="text-sm font-medium mb-3">Available Workflows</h3>
            <div className="space-y-2">
              {workflows.length > 0 ? (
                workflows.map(workflow => (
                  <button
                    key={workflow.id}
                    onClick={() => executeWorkflow(workflow)}
                    disabled={isLoading}
                    className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                      currentWorkflow?.id === workflow.id
                        ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                    } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      <Workflow size={14} />
                      <span className="truncate">{workflow.name}</span>
                    </div>
                    {workflow.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                        {workflow.description}
                      </p>
                    )}
                  </button>
                ))
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  No workflows available
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Connection Status */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 text-xs">
            <div className={`w-2 h-2 rounded-full ${
              connectionStatus === 'connected' ? 'bg-green-500' :
              connectionStatus === 'error' ? 'bg-red-500' :
              'bg-gray-400'
            }`} />
            <span className="text-gray-500 dark:text-gray-400">
              {connectionStatus === 'connected' ? 'Connected' :
               connectionStatus === 'error' ? 'Connection Error' :
               'Disconnected'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Chat Playground</h2>
              {currentWorkflow && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Running: {currentWorkflow.name}
                </p>
              )}
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                <X size={20} />
              </button>
            )}
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {messages.map(message => (
            <div key={message.id}>
              {renderMessage(message)}
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start mb-4">
              <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg px-4 py-2 border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="flex items-center gap-2">
                  <Loader className="animate-spin text-blue-500" size={14} />
                  <span className="text-sm">Processing...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
              placeholder="Type your message here..."
              disabled={isLoading || !sessionId}
              className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
            />
            <button
              onClick={sendMessage}
              disabled={isLoading || !inputMessage.trim() || !sessionId}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Send size={16} />
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatRenderer;